import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useAppDispatch, useAppSelector } from "../redux/store";
import { Tooltip, IconButton, Typography, useTheme } from "@mui/material";
import { tokens } from "../theme";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";

import PageContainer from "../layouts/PageContainer";
import PageToolbar from "../components/common/PageToolbar";
import DateRangeFilter from "../components/common/DateRangeFilter";
import StandardTable, { TableColumn } from "../components/common/StandardTable";
import ViewSaleModal from "../components/sales/ViewSaleModal";

import useDebounce from "../hooks/useDebounce";
import { usePermissions } from "../hooks/usePermissions";
import { getSales, getSaleById } from "../redux/actions/saleActions";
import { SaleSummaryDto } from "../shared/dtos/sale/SaleSummaryDto";
import { formatPlainNumber } from "../utils/appUtils";

const SalesPage: React.FC = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const dispatch = useAppDispatch();
  const { hasPermission } = usePermissions();

  const today = new Date().toISOString().split("T")[0];

  // Redux State
  const {
    sales,
    pagination,
    loadingFetchAll: loadingSales,
    errorFetchAll: errorLoadingSales,
    sale,
    loadingFetchOne: loadingSale,
    errorFetchOne: errorLoadingSale,
  } = useAppSelector((state) => state.sale);

  // Local State
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearch = useDebounce(searchTerm, 500);
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(today);
  const [pageConfig, setPageConfig] = useState({ page: 1, limit: 10 });
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);

  const canRead = hasPermission("sale:read_all");

  const fetchSalesData = useCallback(() => {
    if (!canRead) return;
    dispatch(
      getSales({
        page: pageConfig.page,
        size: pageConfig.limit,
        search: debouncedSearch,
        startDate,
        endDate,
      }),
    );
  }, [
    dispatch,
    pageConfig.page,
    pageConfig.limit,
    debouncedSearch,
    startDate,
    endDate,
    canRead,
  ]);

  useEffect(() => {
    fetchSalesData();
  }, [fetchSalesData]);

  // Reset pagination to page 1 on filter changes
  useEffect(() => {
    setPageConfig((p) => ({ ...p, page: 1 }));
  }, [debouncedSearch, startDate, endDate]);

  const handleViewSale = (id: string): void => {
    dispatch(getSaleById(id));
    setIsViewModalOpen(true);
  };

  const columns: TableColumn<SaleSummaryDto>[] = useMemo(
    () => [
      // --- DEFAULT VISIBLE COLUMNS ---
      {
        id: "shiftStartTime",
        label: "Date",
        minWidth: 150,
        format: (_, row) => new Date(row.shiftStartTime).toLocaleString(),
      },
      {
        id: "cashierName",
        label: "Cashier",
        minWidth: 120,
        format: (_, row) => (
          <Typography sx={{ fontWeight: "bold" }}>{row.cashierName}</Typography>
        ),
      },
      {
        id: "receiptCount",
        label: "Docs",
        minWidth: 150,
        format: (_, row) =>
          `${row.receiptCount} Rec / ${row.invoiceCount} Inv / ${row.creditNoteCount} CN`,
      },
      {
        id: "salesTotal",
        label: "Gross Sales",
        minWidth: 130,
        align: "right",
        format: (_, row) => (
          <Typography variant="body2" sx={{ fontWeight: "bold" }}>
            {formatPlainNumber(row.salesTotal)}
          </Typography>
        ),
      },
      {
        id: "totalTax",
        label: "Tax",
        minWidth: 110,
        align: "right",
        format: (_, row) => formatPlainNumber(row.totalTax),
      },
      {
        id: "salesProfit",
        label: "Net Profit",
        minWidth: 130,
        align: "right",
        format: (_, row) => (
          <Typography
            variant="body2"
            sx={{ fontWeight: "bold", color: colors.green[500] }}
          >
            {formatPlainNumber(row.salesProfit)}
          </Typography>
        ),
      },

      // --- HIDDEN BY DEFAULT ---
      {
        id: "totalDiscount",
        label: "Discount",
        minWidth: 110,
        align: "right",
        defaultHidden: true,
        format: (_, row) => formatPlainNumber(row.totalDiscount),
      },
    ],
    [colors],
  );

  const renderRowActions = (row: SaleSummaryDto): React.JSX.Element => (
    <Tooltip title="View Detailed Report">
      <IconButton
        size="small"
        color="primary"
        onClick={() => handleViewSale(row.id)}
      >
        <VisibilityOutlinedIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  );

  return (
    <PageContainer
      title="Sales Reports"
      subtitle="Analyze sales performance, tax collection, and profitability by shift."
    >
      <PageToolbar
        searchTerm={searchTerm}
        onSearchChange={(e) => setSearchTerm(e.target.value)}
        searchPlaceholder="Search by cashier..."
      >
        <DateRangeFilter
          startDate={startDate}
          setStartDate={setStartDate}
          endDate={endDate}
          setEndDate={setEndDate}
        />
      </PageToolbar>

      <StandardTable
        columns={columns}
        data={sales}
        loading={loadingSales}
        error={errorLoadingSales}
        onRetry={fetchSalesData}
        page={pageConfig.page}
        count={pagination?.totalElements || 0}
        rowsPerPage={pageConfig.limit}
        onPageChange={(p) => setPageConfig({ ...pageConfig, page: p })}
        onRowsPerPageChange={(l) =>
          setPageConfig({ ...pageConfig, limit: l, page: 1 })
        }
        enableColumnManagement={true}
        rowAction={renderRowActions}
      />

      {isViewModalOpen && (
        <ViewSaleModal
          open={isViewModalOpen}
          onClose={() => setIsViewModalOpen(false)}
          sale={sale}
          loading={loadingSale}
          error={errorLoadingSale}
        />
      )}
    </PageContainer>
  );
};

export default SalesPage;
